import * as React from 'react'
import type { Resource, ResourceFail, ResourceFulfilled, ResourceInit, ResourceRejected } from '../../types/Resource'
import type { View, ViewDone, ViewFail } from '../../types/SceneViewAware'
import { MasterState } from './MasterState'
import { MasterStateWrapper } from './MasterStateWrapper'

export interface TresourceConfig<Tdata, Textra, TdataRejected, TdataFail, TisSubmitted, TcanReject, TcanFail> {
    resource: Resource<Tdata, Textra, TdataRejected, TdataFail>
    isSubmitted: TisSubmitted
    canReject: TcanReject
    canFail: TcanFail
}

type addRejected<TdataRejected, TcanReject> = TcanReject extends true ? ResourceRejected<TdataRejected> : never
type addFail<TdataFail, TcanFail> = TcanFail extends true ? ResourceFail<TdataFail> : never
type addInit<TisSubmitted> = TisSubmitted extends true ? ResourceInit : never

export interface TviewConfig<TViewDone extends ViewDone<unknown, unknown>, TcanFail> {
    view: View<TViewDone>
    canFail: TcanFail
}

type addViewFail<TcanFail> = TcanFail extends true ? ViewFail : never

type reactNodeReturn = React.ReactElement

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
    T2data,
    T2extra,
    T2dataRejected,
    T2dataFail,
    T2isSubmitted extends boolean,
    T2canReject extends boolean,
    T2canFail extends boolean,
    TviewDone extends ViewDone<unknown, unknown>,
    TviewCanFail extends boolean,
>(
    configs: [
        TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>,
        TresourceConfig<T2data, T2extra, T2dataRejected, T2dataFail, T2isSubmitted, T2canReject, T2canFail>,
        TviewConfig<TviewDone, TviewCanFail>,
    ],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>,
        r2:
            | ResourceFulfilled<T2data, T1extra>
            | addInit<T2isSubmitted>
            | addRejected<T2dataRejected, T2canReject>
            | addFail<T2dataFail, T2canFail>,
        v1: TviewDone | addViewFail<TviewCanFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
    TviewDone extends ViewDone<unknown, unknown>,
    TviewCanFail extends boolean,
>(
    configs: [
        TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>,
        TviewConfig<TviewDone, TviewCanFail>,
    ],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>,
        v1: TviewDone | addViewFail<TviewCanFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
    T2data,
    T2extra,
    T2dataRejected,
    T2dataFail,
    T2isSubmitted extends boolean,
    T2canReject extends boolean,
    T2canFail extends boolean,
    T3data,
    T3extra,
    T3dataRejected,
    T3dataFail,
    T3isSubmitted extends boolean,
    T3canReject extends boolean,
    T3canFail extends boolean,
    T4data,
    T4extra,
    T4dataRejected,
    T4dataFail,
    T4isSubmitted extends boolean,
    T4canReject extends boolean,
    T4canFail extends boolean,
    T5data,
    T5extra,
    T5dataRejected,
    T5dataFail,
    T5isSubmitted extends boolean,
    T5canReject extends boolean,
    T5canFail extends boolean,
>(
    configs: [
        TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>,
        TresourceConfig<T2data, T2extra, T2dataRejected, T2dataFail, T2isSubmitted, T2canReject, T2canFail>,
        TresourceConfig<T3data, T3extra, T3dataRejected, T3dataFail, T3isSubmitted, T3canReject, T3canFail>,
        TresourceConfig<T4data, T4extra, T4dataRejected, T4dataFail, T4isSubmitted, T4canReject, T4canFail>,
        TresourceConfig<T5data, T5extra, T5dataRejected, T5dataFail, T5isSubmitted, T5canReject, T5canFail>,
    ],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>,
        r2:
            | ResourceFulfilled<T2data, T2extra>
            | addInit<T2isSubmitted>
            | addRejected<T2dataRejected, T2canReject>
            | addFail<T2dataFail, T2canFail>,
        r3:
            | ResourceFulfilled<T3data, T3extra>
            | addInit<T3isSubmitted>
            | addRejected<T3dataRejected, T3canReject>
            | addFail<T3dataFail, T3canFail>,
        r4:
            | ResourceFulfilled<T4data, T4extra>
            | addInit<T4isSubmitted>
            | addRejected<T4dataRejected, T4canReject>
            | addFail<T4dataFail, T4canFail>,
        r5:
            | ResourceFulfilled<T5data, T5extra>
            | addInit<T5isSubmitted>
            | addRejected<T5dataRejected, T5canReject>
            | addFail<T5dataFail, T5canFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
    T2data,
    T2extra,
    T2dataRejected,
    T2dataFail,
    T2isSubmitted extends boolean,
    T2canReject extends boolean,
    T2canFail extends boolean,
    T3data,
    T3extra,
    T3dataRejected,
    T3dataFail,
    T3isSubmitted extends boolean,
    T3canReject extends boolean,
    T3canFail extends boolean,
    T4data,
    T4extra,
    T4dataRejected,
    T4dataFail,
    T4isSubmitted extends boolean,
    T4canReject extends boolean,
    T4canFail extends boolean,
>(
    configs: [
        TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>,
        TresourceConfig<T2data, T2extra, T2dataRejected, T2dataFail, T2isSubmitted, T2canReject, T2canFail>,
        TresourceConfig<T3data, T3extra, T3dataRejected, T3dataFail, T3isSubmitted, T3canReject, T3canFail>,
        TresourceConfig<T4data, T4extra, T4dataRejected, T4dataFail, T4isSubmitted, T4canReject, T4canFail>,
    ],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>,
        r2:
            | ResourceFulfilled<T2data, T2extra>
            | addInit<T2isSubmitted>
            | addRejected<T2dataRejected, T2canReject>
            | addFail<T2dataFail, T2canFail>,
        r3:
            | ResourceFulfilled<T3data, T3extra>
            | addInit<T3isSubmitted>
            | addRejected<T3dataRejected, T3canReject>
            | addFail<T3dataFail, T3canFail>,
        r4:
            | ResourceFulfilled<T4data, T4extra>
            | addInit<T4isSubmitted>
            | addRejected<T4dataRejected, T4canReject>
            | addFail<T4dataFail, T4canFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
    T2data,
    T2extra,
    T2dataRejected,
    T2dataFail,
    T2isSubmitted extends boolean,
    T2canReject extends boolean,
    T2canFail extends boolean,
    T3data,
    T3extra,
    T3dataRejected,
    T3dataFail,
    T3isSubmitted extends boolean,
    T3canReject extends boolean,
    T3canFail extends boolean,
>(
    configs: [
        TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>,
        TresourceConfig<T2data, T2extra, T2dataRejected, T2dataFail, T2isSubmitted, T2canReject, T2canFail>,
        TresourceConfig<T3data, T3extra, T3dataRejected, T3dataFail, T3isSubmitted, T3canReject, T3canFail>,
    ],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>,
        r2:
            | ResourceFulfilled<T2data, T2extra>
            | addInit<T2isSubmitted>
            | addRejected<T2dataRejected, T2canReject>
            | addFail<T2dataFail, T2canFail>,
        r3:
            | ResourceFulfilled<T3data, T3extra>
            | addInit<T3isSubmitted>
            | addRejected<T3dataRejected, T3canReject>
            | addFail<T3dataFail, T3canFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
    T2data,
    T2extra,
    T2dataRejected,
    T2dataFail,
    T2isSubmitted extends boolean,
    T2canReject extends boolean,
    T2canFail extends boolean,
>(
    configs: [
        TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>,
        TresourceConfig<T2data, T2extra, T2dataRejected, T2dataFail, T2isSubmitted, T2canReject, T2canFail>,
    ],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>,
        r2:
            | ResourceFulfilled<T2data, T2extra>
            | addInit<T2isSubmitted>
            | addRejected<T2dataRejected, T2canReject>
            | addFail<T2dataFail, T2canFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder<
    T1data,
    T1extra,
    T1dataRejected,
    T1dataFail,
    T1isSubmitted extends boolean,
    T1canReject extends boolean,
    T1canFail extends boolean,
>(
    configs: [TresourceConfig<T1data, T1extra, T1dataRejected, T1dataFail, T1isSubmitted, T1canReject, T1canFail>],
    whenDone: (
        r1:
            | ResourceFulfilled<T1data, T1extra>
            | addInit<T1isSubmitted>
            | addRejected<T1dataRejected, T1canReject>
            | addFail<T1dataFail, T1canFail>
    ) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn

export function masterStateBuilder(
    configs: inConfigType[],
    whenDone: (...rs: any) => React.ReactNode | React.ReactNode[],
    customization?: MasterState.Props['customization'],
    onlyOne?: boolean
): reactNodeReturn {
    if (onlyOne) {
        return <MasterStateWrapper configs={configs} whenDone={whenDone} customization={customization} />
    }

    const resourcesConfig = configs.filter((c: any) => typeof c.resource !== 'undefined') as TresourceConfig<
        unknown,
        unknown,
        unknown,
        unknown,
        unknown,
        unknown,
        unknown
    >[]

    const viewsConfig = configs.filter((c: any) => typeof c.view !== 'undefined') as TviewConfig<
        ViewDone<unknown, unknown>,
        unknown
    >[]

    return (
        <MasterState resourceStates={resourcesConfig} viewStates={viewsConfig} customization={customization}>
            {() => whenDone(...resourcesConfig.map(r => r.resource), ...viewsConfig.map(v => v.view))}
        </MasterState>
    )
}

export type inConfigType =
    | TresourceConfig<unknown, unknown, unknown, unknown, unknown, unknown, unknown>
    | TviewConfig<ViewDone<unknown, unknown>, unknown>
